import { IpaType } from "../../phonemes/ipa";
import { PhonotacticsInput } from "../../phonotactics/wordGenerator";

/**
 * convert phonotactics to a simple to understand string representation,
 * can do `otherOnsets: firstOnsets` or `otherCodas: lastCodas`
 */
export function phonotacticsToString(p: PhonotacticsInput): string {
  const syllables = syllablesToString(p.syllableCounts);
  const firstOnsets = asString(p.firstOnsets);
  const otherOnsets = asString(p.otherOnsets);
  const nucleus = asString(p.nucleus);
  const lastCodas = asString(p.lastCodas);
  const otherCodas = asString(p.otherCodas);
  return `syllables: ${syllables}
stressSyllable: ${p.stressSyllable}
secondaryStress: ${p.secondaryStress ?? '0'}

firstOnsets: ${firstOnsets}
otherOnsets: ${otherOnsets === firstOnsets ? 'firstOnsets' : otherOnsets}
nucleus: ${nucleus}
lastCodas: ${lastCodas}
otherCodas: ${otherCodas === lastCodas ? 'lastCodas' : otherCodas}`;
}

function asString(weights: IpaType[] | Record<IpaType, number>): string {
  if (Array.isArray(weights)) {
    return weights.length === 0 ? '∅' : weights.join(' ');
  }
  return Object.keys(weights).map(k => k === '' ? '∅' : k).join(' ');
}

function syllablesToString(syllableCounts?: Record<number, number>): string {
  const toCount = syllableCounts ?? { 2: 1 };
  const counts = Object.keys(toCount).map(s => Number.parseInt(s));
  counts.sort((a, b) => toCount[b] - toCount[a]);
  return counts.map(n => n.toString()).join(' ');
}


/**
 * opposite of phonotacticsToString
 */
export function phonotacticsFromString(s: string): PhonotacticsInput {
  const syllableCounts = getSyllables(s);
  const stressSyllable = getStressSyllable(s, 'stressSyllable');
  const secondaryStress = getStressSyllable(s, 'secondaryStress');
  const firstOnsets = getPhonemes(s, 'firstOnsets');
  const otherOnsets = getPhonemes(s, 'otherOnsets');
  const nucleus = getPhonemes(s, 'nucleus');
  const lastCodas = getPhonemes(s, 'lastCodas');
  const otherCodas = getPhonemes(s, 'otherCodas');
  return {
    syllableCounts,
    stressSyllable,
    secondaryStress,
    firstOnsets,
    otherOnsets: otherOnsets[0] === 'firstOnsets' ? firstOnsets : otherOnsets,
    nucleus,
    lastCodas,
    otherCodas: otherCodas[0] === 'lastCodas' ? lastCodas : otherCodas,
  }
}

function getPhonemes(s: string, section: string): IpaType[] {
  const i = s.indexOf(section + ':');
  if (i < 0) {
    return [];
  }
  const end = s.indexOf('\n', i);
  const piece = s.substring(i + section.length + 1, end > 0 ? end : undefined).trim();
  const phonemes = piece.split(' ');
  return phonemes;
}

function getSyllables(s: string): Record<number, number> | undefined {
  const pieces = getPhonemes(s, 'syllables');
  if (pieces.length === 0) {
    return undefined;
  }

  const result: Record<IpaType, number> = {};
  pieces.forEach((s, i) => { result[s] = pieces.length - i; })
  return result;
}

function getStressSyllable(s: string, key: string): number {
  const i = s.indexOf(key);
  if (i < 0) {
    return 1;
  }
  const end = s.indexOf('\n', i);
  const piece = s.substring(i + key.length + 1, end > 0 ? end : undefined).trim();
  return Number.parseInt(piece);
}
